import React, { useEffect } from "react";
import { motion } from "framer-motion";
import AOS from "aos";
import "aos/dist/aos.css";

export default function TestimonialsPage() {
  useEffect(() => {
    AOS.init({ duration: 900, once: true });
  }, []);

  const testimonials = [
    {
      name: "Daniel Thompson",
      role: "Business Owner",
      text: "The Kaizen Business Solutions delivered a high-performance website that completely changed the way our clients interact with our brand. Professional, fast, and reliable.",
      avatar: "https://randomuser.me/api/portraits/men/32.jpg",
    },
    {
      name: "Sarah Williams",
      role: "Marketing Director",
      text: "Their digital marketing strategy helped us scale our campaigns and increase conversions. We saw real results within the first month!",
      avatar: "https://randomuser.me/api/portraits/women/44.jpg",
    },
    {
      name: "James Carter",
      role: "IT Manager",
      text: "Exceptional IT support. Quick response times and expert solutions. They helped stabilize our systems and reduce downtime drastically.",
      avatar: "https://randomuser.me/api/portraits/men/68.jpg",
    },
    {
      name: "Laura Mitchell",
      role: "Entrepreneur",
      text: "Their guidance in SEO and website optimization pushed my business ahead of competitors. Honest, transparent, and effective work.",
      avatar: "https://randomuser.me/api/portraits/women/65.jpg",
    },
  ];

  return (
    <div className="text-white bg-gray-950 min-h-screen">

      {/* Mini Hero Section */}
      <motion.section
        initial={{ opacity: 0, y: -40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9 }}
        className="relative h-[45vh] flex items-center justify-center text-center px-6 overflow-hidden"
      >
        {/* Gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-cyan-600/30 to-blue-900/40 backdrop-blur-md"></div>

        {/* Floating glow lights */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.2 }}
          className="absolute top-10 left-16 w-60 h-60 bg-cyan-500/20 blur-3xl rounded-full"
        ></motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.2, delay: 0.2 }}
          className="absolute bottom-10 right-16 w-72 h-72 bg-blue-600/20 blur-3xl rounded-full"
        ></motion.div>

        <div className="relative z-10">

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="text-4xl md:text-6xl font-bold"
          >
            What Our Clients <span className="text-cyan-400">Say About Us</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="max-w-2xl mx-auto text-gray-300 text-lg mt-4"
          >
            Our commitment to quality and innovation has earned the trust of businesses worldwide.
          </motion.p>
        </div>
      </motion.section>

      {/* Testimonials List */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <motion.h2
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
          className="text-3xl md:text-4xl font-bold text-center"
        >
          Trusted by Clients Across Different Industries
        </motion.h2>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.8 }}
          className="text-gray-400 text-center mt-4 max-w-2xl mx-auto"
        >
          Here are some of the experiences shared by companies and entrepreneurs who trusted The Kaizen Business Solutions.
        </motion.p>

        <div className="grid gap-10 md:grid-cols-2 lg:grid-cols-3 mt-16">
          {testimonials.map((t, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0.8, y: 30 }}
              whileInView={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 0.7, delay: index * 0.15 }}
              viewport={{ once: true }}
              className="p-8 bg-gray-900/60 border border-gray-800 rounded-2xl shadow-lg backdrop-blur-lg
                hover:shadow-cyan-500/20 hover:-translate-y-1 transition-all duration-300"
            >
              <img
                src={t.avatar}
                alt={t.name}
                className="w-20 h-20 rounded-full mx-auto shadow-md border border-gray-700"
              />

              <p className="text-gray-300 mt-6 italic">"{t.text}"</p>

              <h3 className="mt-6 text-xl font-semibold text-white">{t.name}</h3>
              <span className="text-sm text-gray-400">{t.role}</span>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <motion.section
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 1 }}
        className="py-24 text-center bg-gradient-to-b from-gray-900 to-gray-950"
      >
        <h2 className="text-3xl md:text-5xl font-bold">
          Ready to Work With a Team You Can Trust?
        </h2>
        <p className="text-gray-400 mt-4 max-w-xl mx-auto">
          Let’s build something exceptional together — from websites to IT systems, digital marketing, and SEO.
        </p>

        <motion.a
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.97 }}
          href="/contact"
          className="inline-block mt-8 px-10 py-4 bg-cyan-500 text-gray-900 font-semibold rounded-xl
            hover:bg-cyan-400 transition-all shadow-lg"
        >
          Get a Free Quote
        </motion.a>
      </motion.section>
    </div>
  );
}
